/**
 * THE ONLY MODULE THAT NAMES ms-agents AUDIENCE-FILTER RESPONSE FIELDS.
 *
 * `broadcastDecode.js` owns the v1_10_0 response surface (broadcasts, funnels,
 * recipients, estimates). This file owns the v1_11_0 one that arrived with
 * saved audience filters: the field registry, the saved-filter records, the
 * live preview, the presets catalogue and the install-defaults result. The two
 * are split by API surface rather than merged because they version separately —
 * the filters routes are additive and can move without touching a broadcast
 * decoder — and because one 700-line decoder is how a decoder stops being read.
 *
 * Like its siblings it is both a wire owner (it may spell `push_reachable`,
 * `backed_by_data`, `refreshed_at`, `created_at`, …) and a pure module (it may
 * not read storage, React, the clock or the network). If you find yourself
 * typing one of those names in a hook or a component, it belongs here.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THAT MATTERS MOST — INHERITED VERBATIM
 * ---------------------------------------------------------------------------
 * AN ABSENT NUMERIC DECODES TO `null`. A PRESENT ZERO DECODES TO `0`.
 *
 * It matters more here than it did on the list screen. A preview answers "how
 * many people does this filter reach"; `matched: 0` means "we counted, and it
 * is nobody" and an absent `matched` means "we have not counted yet". Those are
 * different answers to the same question, and collapsing them puts the author
 * one click from cancelling a filter that was fine or sending one that reaches
 * no one. Every numeric below goes through `num()`; there is no zero-coalescing
 * anywhere in this file, and the test greps this source to prove it — which is
 * also why the two banned operators are described here rather than spelled.
 *
 * ---------------------------------------------------------------------------
 * THE SECOND RULE — A CAPABILITY FLAG DEFAULTS TO TRUE, BUT ONLY WHEN ABSENT
 * ---------------------------------------------------------------------------
 * `available` (on a field) and `backed_by_data` (on a preset) are the server
 * telling us a filter it will happily accept would nonetheless match nobody,
 * because the table behind it is empty. `location_id` is the live example: it
 * is wired end to end — registry, validation, SQL — and its upstream table has
 * no rows, so a rule built on it silently matches zero people and the author
 * has no way to see why. Offering that field is worse than hiding it.
 *
 * So an explicit `false` MUST survive decoding. And an ABSENT key must default
 * to `true`, because a backend that predates the flag has no unavailable fields
 * to warn us about — defaulting to false there would blank the entire builder
 * against a server that is working perfectly. `flagDefaultingTrue()` is the
 * only place that distinction is made, and both halves of it are asserted.
 *
 * PURE — no React, no localStorage, no `Date.now()`, no network. `refreshedAt`
 * is handed back as the raw string the server sent; deciding that "2 minutes
 * ago" is the caller's job and needs a clock this file must not read.
 */

/* ── Primitives ───────────────────────────────────────────────────────── */

/*
 * These four are character-for-character the ones in `broadcastDecode.js`.
 * They are copied rather than imported because they are module-private there,
 * and widening a decoder's public surface so a sibling can borrow its helpers
 * would make both modules' exports a mix of contract and plumbing. The copy is
 * cheap; the property that matters — no numeric ever defaults to zero — is
 * enforced by a source grep in each module's own test, so neither copy can
 * drift into the behaviour the rule exists to forbid.
 */

/**
 * Read the first present value among several server naming conventions.
 *
 * @param {Object} record
 * @param {string[]} keys
 * @returns {*} undefined when no key carries a value.
 */
const pick = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

/**
 * THE helper. Absent → null, present → Number.
 *
 * Empty string counts as absent: `Number("")` is 0, which is the exact lie
 * this file exists to prevent.
 *
 * @param {*} value
 * @returns {number|null}
 */
const num = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Absent → null, present → String. Never the empty string.
 *
 * @param {*} value
 * @returns {string|null}
 */
const text = (value) => {
  if (value === undefined || value === null) return null;
  const string = String(value).trim();
  return string === "" ? null : string;
};

/** Lowercased text, for closed enums the server may send in either casing. */
const slug = (value) => {
  const string = text(value);
  return string === null ? null : string.toLowerCase();
};

/** Strings the wire uses for false. JSON should not need this; CSV exports do. */
const FALSE_WORDS = new Set(["false", "0", "no", "null"]);

/**
 * Coerce to a real boolean. `Boolean("false")` is true, which is how a flag
 * round-tripped through a query string reads as its own opposite.
 *
 * @param {*} value
 * @returns {boolean}
 */
const bool = (value) => {
  if (typeof value === "string") return !FALSE_WORDS.has(value.trim().toLowerCase());
  return Boolean(value);
};

/**
 * A capability flag: ABSENT → true, present → the boolean it really is.
 *
 * The asymmetry is the whole point, and it is not the same shape as `num()`.
 * `num()` refuses to invent a measurement; this refuses to invent a WARNING.
 * A server that never sends `available` is not telling us the field is broken,
 * it is telling us nothing — and "nothing" about a field that exists in the
 * registry means "use it". An explicit `false`, by contrast, is a deliberate
 * statement that must reach the UI intact, because it is the only signal that
 * separates "this filter will match nobody" from "this filter is fine".
 *
 * @param {*} value
 * @returns {boolean}
 */
const flagDefaultingTrue = (value) =>
  value === undefined || value === null ? true : bool(value);

/** Pull a list out of whichever envelope the backend used. */
const unwrapList = (payload, primaryKey) => {
  if (Array.isArray(payload)) return payload;
  const list =
    payload?.[primaryKey] || payload?.items || payload?.results || payload?.data;
  return Array.isArray(list) ? list : [];
};

/**
 * A bare list of scalars, as strings.
 *
 * Lists are the one place this file collapses absence into emptiness, matching
 * `unwrapList` above: nothing downstream renders "we don't know which fields
 * this preset requires" differently from "it requires none", so inventing a
 * third state would buy a null check at every call site and nothing else.
 *
 * @param {*} value
 * @returns {string[]}
 */
const stringList = (value) =>
  Array.isArray(value) ? value.map((entry) => String(entry)) : [];

/** `total` under either of its two names. */
const totalOf = (payload) => num(pick(payload, ["total", "count"]));

/* ── Field registry ───────────────────────────────────────────────────── */

/**
 * One filterable field, as the rule builder's first dropdown renders it.
 *
 * `type` drives everything downstream: `operatorsFor(type)` in
 * `broadcastEnums.js` decides which operators the second dropdown offers, and
 * `valueKindFor(type, op)` decides what the third control is. So it is
 * lowercased here — a registry that answered "Number" once and "number" the
 * next time would silently offer an empty operator list.
 *
 * @param {Object} record
 * @returns {Object}
 */
const fieldFromRecord = (record = {}) => ({
  // Older builds of the registry named this `field`; v1_11_0 names it `key`.
  key: text(pick(record, ["key", "field"])),
  label: text(record.label),
  type: slug(record.type),
  // See the header: an explicit false is a warning, an absent key is silence.
  available: flagDefaultingTrue(record.available),
  // Enum options pass through untouched. They are DATA — the actual ids the
  // rule will carry — not vocabulary, so reshaping them here would mean
  // reshaping them back before the rule is sent.
  options: Array.isArray(record.options) ? record.options : [],
  description: text(record.description),
});

/**
 * GET /api/v1/broadcasts/filters/fields.
 *
 * @param {Object|Array} payload
 * @returns {{ fields: Object[] }}
 */
export const fieldsFromPayload = (payload) => ({
  fields: unwrapList(payload, "fields").map((record) => fieldFromRecord(record)),
});

/* ── Saved filters ────────────────────────────────────────────────────── */

/**
 * One rule of a saved filter.
 *
 * `value` is passed through VERBATIM and deliberately never coerced. It is a
 * string for a text rule, a number for `gte`, an array of ids for `in`, and a
 * two-element `[lo, hi]` tuple for `between`. Running it through `num()` would
 * turn `[3, 7]` into NaN and then into null; running it through `text()` would
 * turn the same tuple into the string "3,7". Either one destroys the rule while
 * looking like it decoded it.
 *
 * @param {Object} record
 * @returns {{ field: string|null, op: string|null, value: * }}
 */
const ruleFromRecord = (record = {}) => ({
  field: text(record.field),
  op: slug(record.op),
  value: record.value === undefined ? null : record.value,
});

/**
 * One saved filter.
 *
 * @param {Object} record
 * @returns {Object}
 */
export const filterFromRecord = (record = {}) => ({
  id: text(record.id),
  name: text(record.name),
  rules: Array.isArray(record.rules)
    ? record.rules.map((rule) => ruleFromRecord(rule))
    : [],
  // Defaults to true when absent, for the same reason `available` does: a
  // backend that does not model deactivation sends no flag, and reading that
  // silence as "inactive" would empty a list of perfectly good filters. An
  // explicit false still survives and still hides the filter from the picker.
  isActive: flagDefaultingTrue(record.is_active),
  createdAt: text(record.created_at),
});

/**
 * GET /api/v1/broadcasts/filters.
 *
 * @param {Object|Array} payload
 * @returns {{ filters: Object[], total: number|null }}
 */
export const filtersFromPayload = (payload) => ({
  filters: unwrapList(payload, "filters").map((record) => filterFromRecord(record)),
  total: totalOf(payload),
});

/* ── Preview ──────────────────────────────────────────────────────────── */

/**
 * The `breakdown` map, keyed exactly as the server keyed it.
 *
 * The keys (`ACTIVE`, `PAUSED`, `UNSUBSCRIBED`) are enum VALUES, not field
 * names — they are the server's own segment ids, the same strings
 * `broadcastEnums.js` already uses. Renaming them to camelCase here would
 * invent a vocabulary the API never had and force every consumer to translate
 * back before it could line a bucket up against a segment. The VALUES go
 * through `num()` like every other counter.
 *
 * @param {*} raw
 * @returns {Object|null} null when the server sent no breakdown at all.
 */
const breakdownOf = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return Object.entries(raw).reduce(
    (out, [key, value]) => ({ ...out, [key]: num(value) }),
    {}
  );
};

/**
 * POST /api/v1/broadcasts/filters/preview.
 *
 * `matched` is WHO the rules select. `pushReachable` and `emailReachable` are
 * how many of those the two channels can actually deliver to, and they are
 * both smaller for different reasons — a device token vs. an address and a
 * suppression list. The three are returned exactly as the server computed them
 * and are never derived from one another.
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const previewFromPayload = (payload = {}) => ({
  matched: num(payload.matched),
  pushReachable: num(payload.push_reachable),
  emailReachable: num(payload.email_reachable),
  breakdown: breakdownOf(payload.breakdown),
  // The raw instant, verbatim. A preview can be served from a cache that is
  // minutes old and this string is the only thing that says so; formatting it
  // needs a clock, which a pure module must not read.
  refreshedAt: text(payload.refreshed_at),
});

/* ── Presets ──────────────────────────────────────────────────────────── */

/**
 * One preset from the catalogue.
 *
 * `requires` lists the fields the preset is built on; `missingDataFor` lists
 * the subset of those whose tables are empty. When `backedByData` is false,
 * `missingDataFor` is the answer to "why" — which is why the pair is decoded
 * together and shown together, rather than the preset simply vanishing from
 * the list. A preset that disappears teaches nobody anything.
 *
 * @param {Object} record
 * @returns {Object}
 */
const presetFromRecord = (record = {}) => ({
  name: text(record.name),
  requires: stringList(record.requires),
  // Absent → true, explicit false survives. Same reasoning as `available`.
  backedByData: flagDefaultingTrue(record.backed_by_data),
  missingDataFor: stringList(record.missing_data_for),
  // Only present when the caller asked for counts. Absent stays null: "we did
  // not ask" must not render as "this preset matches nobody".
  matched: num(record.matched),
  pushReachable: num(record.push_reachable),
  emailReachable: num(record.email_reachable),
});

/**
 * GET /api/v1/broadcasts/filters/presets.
 *
 * @param {Object|Array} payload
 * @returns {{ presets: Object[] }}
 */
export const presetsFromPayload = (payload) => ({
  presets: unwrapList(payload, "presets").map((record) => presetFromRecord(record)),
});

/* ── Install defaults ─────────────────────────────────────────────────── */
